// ========================================
// CacaPalavras Component
// ========================================
// Versão: 1.0
// Data: 01 de Novembro de 2025

import { useState, useEffect } from 'react';
import './CacaPalavras.css';

interface Palavra {
  palavra: string;
  inicio: { linha: number; coluna: number };
  fim: { linha: number; coluna: number };
  direcao: 'horizontal' | 'vertical' | 'diagonal';
  significado: string;
  descricao?: string; // Descrição adicional do termo
  encontrada?: boolean;
}

interface CacaPalavrasProps {
  grade: string[][];
  palavras: Palavra[];
  tema: string;
  dificuldade: number;
  onPalavraEncontrada?: (palavra: string) => void;
  onTodasEncontradas?: () => void;
}

export default function CacaPalavras({
  grade,
  palavras,
  tema,
  dificuldade,
  onPalavraEncontrada,
  onTodasEncontradas
}: CacaPalavrasProps) {
  const [palavrasEncontradas, setPalavrasEncontradas] = useState<Set<string>>(new Set());
  const [selecionando, setSelecionando] = useState(false);
  const [celulasTemp, setCelulasTemp] = useState<Set<string>>(new Set());
  const [celulasEncontradas, setCelulasEncontradas] = useState<Set<string>>(new Set());
  const [inicioSelecao, setInicioSelecao] = useState<{ linha: number; coluna: number } | null>(null);
  const [jogoFinalizado, setJogoFinalizado] = useState(false);

  // Verificar se todas as palavras foram encontradas
  useEffect(() => {
    if (palavrasEncontradas.size === palavras.length && palavrasEncontradas.size > 0 && !jogoFinalizado) {
      setJogoFinalizado(true);
      console.log('[CACA-PALAVRAS-COMPONENT] Todas as palavras encontradas!');
      
      if (onTodasEncontradas) {
        // Aguardar um pouco para garantir animação final
        setTimeout(() => {
          onTodasEncontradas();
        }, 500);
      }
    }
  }, [palavrasEncontradas, palavras.length, onTodasEncontradas, jogoFinalizado]);

  const getCelulaKey = (linha: number, coluna: number) => `${linha}-${coluna}`;

  const handleMouseDown = (linha: number, coluna: number) => {
    setSelecionando(true);
    setInicioSelecao({ linha, coluna });
    setCelulasTemp(new Set([getCelulaKey(linha, coluna)]));
  };

  const handleMouseEnter = (linha: number, coluna: number) => {
    if (!selecionando || !inicioSelecao) return;

    // Calcular células entre início e posição atual
    const celulas = calcularCelulasEntre(inicioSelecao.linha, inicioSelecao.coluna, linha, coluna);
    setCelulasTemp(celulas);
  };

  const handleMouseUp = () => {
    if (!selecionando || celulasTemp.size === 0) {
      setSelecionando(false);
      return;
    }

    // Verificar se a seleção forma uma palavra válida
    const palavraEncontrada = verificarPalavra(Array.from(celulasTemp));

    if (palavraEncontrada) {
      // Palavra encontrada!
      setPalavrasEncontradas(prev => new Set([...prev, palavraEncontrada.palavra]));
      setCelulasEncontradas(prev => new Set([...prev, ...celulasTemp]));
      
      if (onPalavraEncontrada) {
        onPalavraEncontrada(palavraEncontrada.palavra);
      }
    }

    // Reset
    setSelecionando(false);
    setCelulasTemp(new Set());
    setInicioSelecao(null);
  };

  const calcularCelulasEntre = (
    linha1: number,
    coluna1: number,
    linha2: number,
    coluna2: number
  ): Set<string> => {
    const celulas = new Set<string>();
    const deltaLinha = linha2 - linha1;
    const deltaColuna = coluna2 - coluna1;

    // Determinar direção
    const isHorizontal = deltaLinha === 0;
    const isVertical = deltaColuna === 0;
    const isDiagonal = Math.abs(deltaLinha) === Math.abs(deltaColuna);

    if (!isHorizontal && !isVertical && !isDiagonal) {
      // Seleção inválida
      return new Set([getCelulaKey(linha1, coluna1)]);
    }

    const steps = Math.max(Math.abs(deltaLinha), Math.abs(deltaColuna));
    const stepLinha = deltaLinha === 0 ? 0 : deltaLinha / Math.abs(deltaLinha);
    const stepColuna = deltaColuna === 0 ? 0 : deltaColuna / Math.abs(deltaColuna);

    for (let i = 0; i <= steps; i++) {
      const linha = linha1 + i * stepLinha;
      const coluna = coluna1 + i * stepColuna;
      celulas.add(getCelulaKey(linha, coluna));
    }

    return celulas;
  };

  const verificarPalavra = (celulas: string[]): Palavra | null => {
    // Extrair letras das células selecionadas
    const letras = celulas.map(key => {
      const [linha, coluna] = key.split('-').map(Number);
      return grade[linha][coluna];
    }).join('');

    // Verificar se forma alguma palavra (normal ou reversa)
    for (const palavra of palavras) {
      if (palavrasEncontradas.has(palavra.palavra)) continue; // Já encontrada

      if (letras === palavra.palavra || letras === palavra.palavra.split('').reverse().join('')) {
        return palavra;
      }
    }

    return null;
  };

  const renderDifficultyStars = () => {
    return '★'.repeat(Math.min(dificuldade, 10));
  };

  return (
    <div className="cacapalavras-container">
      {/* Cabeçalho */}
      <div className="cacapalavras-header">
        <div>
          <h3>{tema}</h3>
          <span className="badge bg-primary">
            {renderDifficultyStars()} {dificuldade}/10
          </span>
        </div>
        <div className="palavras-counter">
          <strong>{palavrasEncontradas.size}</strong> / {palavras.length} palavras
        </div>
      </div>

      <div className="row g-4">
        {/* Grade */}
        <div className="col-lg-7">
          <div className="grade-container" onMouseLeave={() => { setSelecionando(false); setCelulasTemp(new Set()); }}>
            {grade.map((linha, linhaIdx) => (
              <div key={linhaIdx} className="grade-linha">
                {linha.map((letra, colunaIdx) => {
                  const key = getCelulaKey(linhaIdx, colunaIdx);
                  const isEncontrada = celulasEncontradas.has(key);
                  const isTemp = celulasTemp.has(key);

                  return (
                    <div
                      key={key}
                      className={`grade-celula ${isEncontrada ? 'encontrada' : ''} ${isTemp ? 'temp' : ''}`}
                      onMouseDown={() => handleMouseDown(linhaIdx, colunaIdx)}
                      onMouseEnter={() => handleMouseEnter(linhaIdx, colunaIdx)}
                      onMouseUp={handleMouseUp}
                    >
                      {letra}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Lista de Palavras */}
        <div className="col-lg-5">
          <div className="palavras-lista">
            <h5>📝 Palavras para Encontrar:</h5>
            <ul>
              {palavras.map((palavra, idx) => (
                <li
                  key={idx}
                  className={palavrasEncontradas.has(palavra.palavra) ? 'encontrada' : ''}
                >
                  <div className="palavra-item">
                    <span className="palavra-nome">
                      {palavrasEncontradas.has(palavra.palavra) ? '✅' : '⬜'} <strong>{palavra.palavra}</strong>
                    </span>
                    <div className="palavra-definicao">
                      <small className="palavra-significado">
                        <strong>Significado:</strong> {palavra.significado}
                      </small>
                      {palavra.descricao && (
                        <small className="palavra-descricao">
                          <strong>Descrição:</strong> {palavra.descricao}
                        </small>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
